import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { Scale, RotateCcw, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NANOUK_IMAGES = {
    fit: '/nanouk/energy_fit.png',
    balanced: '/nanouk/energy_balanced.png',
    light: '/nanouk/energy_light.png',
    heavy: '/nanouk/energy_heavy.png',
    crushed: '/nanouk/energy_crushed.png',
};

const STATE_TEXT = {
    fit: { de: 'Nanouk sprintet federleicht – du verbrennst Fett! 🔥', en: 'Nanouk sprints feather-light – you are burning fat! 🔥' },
    balanced: { de: 'Nanouk fühlt sich pudelwohl – alles im Gleichgewicht.', en: 'Nanouk feels great – everything in balance.' },
    light: { de: 'Nanouk trägt schon ein kleines Butter-Säckchen…', en: 'Nanouk is already carrying a small butter pouch…' },
    heavy: { de: 'Nanouk schleppt schwer an deinem Fettpolster! 😰', en: 'Nanouk is struggling under your fat load! 😰' },
    crushed: { de: 'Nanouk ist unter dem Butterberg begraben! Hilf ihm! 🆘', en: 'Nanouk is buried under the butter mountain! Help him! 🆘' },
};

const PERIODS = [
    { days: 7, label: '7T', labelEn: '7d' },
    { days: 30, label: '30T', labelEn: '30d' },
    { days: 90, label: '90T', labelEn: '90d' },
    { days: 0, label: 'Alles', labelEn: 'All' },
];

function formatKcal(v) {
    const sign = v > 0 ? '+' : '';
    return `${sign}${Math.round(v).toLocaleString('de-CH')}`;
}

function ButterRow({ fatKg, language }) {
    if (fatKg > 0.1) {
        const packs = Math.max(1, Math.round(fatKg / 0.25));
        const shown = Math.min(packs, 10);
        return (
            <div className="flex items-center gap-1 flex-wrap" data-testid="energy-butter-row">
                <span className="text-lg leading-none">{'🧈'.repeat(shown)}</span>
                {packs > 10 && <span className="text-xs font-semibold text-amber-700">×{packs}</span>}
                <span className="text-xs text-muted-foreground ml-1">
                    ≈ {fatKg.toFixed(1)} kg {language === 'de' ? 'Fettpolster' : 'fat reserve'} ({packs} {language === 'de' ? 'Päckli Butter' : 'butter packs'})
                </span>
            </div>
        );
    }
    if (fatKg < -0.1) {
        return (
            <div className="flex items-center gap-1" data-testid="energy-butter-row">
                <span className="text-lg leading-none">🔥</span>
                <span className="text-xs text-green-700 font-medium">
                    {Math.abs(fatKg).toFixed(1)} kg {language === 'de' ? 'Fett verbrannt!' : 'fat burned!'}
                </span>
            </div>
        );
    }
    return (
        <span className="text-xs text-muted-foreground" data-testid="energy-butter-row">
            {language === 'de' ? 'Ausgeglichene Bilanz – weiter so!' : 'Balanced – keep it up!'}
        </span>
    );
}

export function EnergyBalanceCard({ compact = false, refreshKey = 0 }) {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (d) => {
        try {
            const res = await axios.get(`${API}/energy/balance`, { params: { days: d } });
            setData(res.data);
        } catch (e) {
            console.error('Energy balance load failed:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(days); }, [days, load, refreshKey]);

    const handleReset = async () => {
        const msg = language === 'de'
            ? 'Energie-Konto wirklich auf Null setzen? Die Aufsummierung startet neu ab heute.'
            : 'Really reset the energy account to zero? Accumulation restarts from today.';
        if (!window.confirm(msg)) return;
        try {
            await axios.post(`${API}/energy/reset`);
            toast.success(language === 'de' ? 'Energie-Konto zurückgesetzt – Neustart! 🚀' : 'Energy account reset – fresh start! 🚀');
            load(days);
        } catch (e) {
            toast.error(language === 'de' ? 'Reset fehlgeschlagen' : 'Reset failed');
        }
    };

    if (loading && !data) return null;
    if (!data?.has_data) {
        if (compact) return null;
        return (
            <Card data-testid="energy-balance-card">
                <CardContent className="p-4 flex items-center gap-3">
                    <img src={NANOUK_IMAGES.balanced} alt="Nanouk" className="w-14 h-14 object-contain" />
                    <p className="text-sm text-muted-foreground">
                        {language === 'de'
                            ? 'Logge deine erste Mahlzeit, dann startet dein Energie-Konto!'
                            : 'Log your first meal to start your energy account!'}
                    </p>
                </CardContent>
            </Card>
        );
    }

    const { cumulative_kcal, fat_kg, nanouk_state, series, tracked_days, start_date } = data;
    const isSurplus = cumulative_kcal > 0;
    const stateText = STATE_TEXT[nanouk_state]?.[language === 'de' ? 'de' : 'en'] || '';

    if (compact) {
        return (
            <Card
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate('/nutrition')}
                data-testid="energy-balance-compact-card"
            >
                <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                        <img src={NANOUK_IMAGES[nanouk_state]} alt="Nanouk" className="w-16 h-16 object-contain shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <Scale className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-semibold">{language === 'de' ? 'Energie-Konto' : 'Energy Account'}</span>
                            </div>
                            <div className={`text-xl font-bold ${isSurplus ? 'text-red-600' : 'text-green-600'}`} data-testid="energy-cumulative-value">
                                {formatKcal(cumulative_kcal)} kcal
                            </div>
                            <ButterRow fatKg={fat_kg} language={language} />
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Split gradient offset so surplus renders red and deficit green
    const values = series.map(p => p.cumulative);
    const max = Math.max(...values, 0);
    const min = Math.min(...values, 0);
    const off = max <= 0 ? 0 : min >= 0 ? 1 : max / (max - min);

    return (
        <Card data-testid="energy-balance-card">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Scale className="w-5 h-5 text-amber-600" />
                        {language === 'de' ? 'Energie-Konto' : 'Energy Account'}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleReset} className="text-muted-foreground h-8" data-testid="energy-reset-btn">
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                    <img src={NANOUK_IMAGES[nanouk_state]} alt="Nanouk" className="w-24 h-24 object-contain shrink-0" data-testid="energy-nanouk-image" />
                    <div className="flex-1 min-w-0 space-y-1">
                        <div className={`text-3xl font-bold ${isSurplus ? 'text-red-600' : 'text-green-600'}`} data-testid="energy-cumulative-value">
                            {formatKcal(cumulative_kcal)} <span className="text-base font-medium">kcal</span>
                        </div>
                        <ButterRow fatKg={fat_kg} language={language} />
                        <p className="text-xs text-muted-foreground italic">{stateText}</p>
                    </div>
                </div>

                <div className="flex gap-1">
                    {PERIODS.map(p => (
                        <Button
                            key={p.days}
                            variant={days === p.days ? 'default' : 'outline'}
                            size="sm"
                            className="h-7 text-xs px-3"
                            onClick={() => setDays(p.days)}
                            data-testid={`energy-period-${p.days}`}
                        >
                            {language === 'de' ? p.label : p.labelEn}
                        </Button>
                    ))}
                </div>

                {series.length > 1 ? (
                    <div className="h-44" data-testid="energy-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={series} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                                <defs>
                                    <linearGradient id="energySplit" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset={off} stopColor="#dc2626" stopOpacity={0.7} />
                                        <stop offset={off} stopColor="#16a34a" stopOpacity={0.7} />
                                    </linearGradient>
                                    <linearGradient id="energySplitFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset={off} stopColor="#dc2626" stopOpacity={0.15} />
                                        <stop offset={off} stopColor="#16a34a" stopOpacity={0.15} />
                                    </linearGradient>
                                </defs>
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10 }}
                                    tickFormatter={(d) => d.slice(5).split('-').reverse().join('.')}
                                    minTickGap={30}
                                />
                                <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={(v) => v.toLocaleString('de-CH')} />
                                <Tooltip
                                    formatter={(value, name) => {
                                        const labels = language === 'de'
                                            ? { cumulative: 'Konto-Saldo', balance: 'Tagesbilanz' }
                                            : { cumulative: 'Balance', balance: 'Daily' };
                                        return [`${formatKcal(value)} kcal`, labels[name] || name];
                                    }}
                                    labelFormatter={(d) => d.split('-').reverse().join('.')}
                                />
                                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
                                <Area type="monotone" dataKey="cumulative" stroke="url(#energySplit)" fill="url(#energySplitFill)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground text-center py-4">
                        {language === 'de' ? 'Noch zu wenig Daten für die Kurve – logge weiter!' : 'Not enough data for the chart yet – keep logging!'}
                    </p>
                )}

                <p className="text-[10px] text-muted-foreground">
                    {language === 'de'
                        ? `Fortlaufende Bilanz seit ${start_date?.split('-').reverse().join('.')} (${tracked_days} getrackte Tage). Aufnahme − Ziel − Sport, kumuliert. 7'700 kcal ≈ 1 kg Körperfett.`
                        : `Running balance since ${start_date?.split('-').reverse().join('.')} (${tracked_days} tracked days). Intake − goal − sport, cumulative. 7,700 kcal ≈ 1 kg body fat.`}
                </p>
            </CardContent>
        </Card>
    );
}
